import { clsx } from 'clsx'
import { type FC, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  type PimDocumentV1,
  PimDocumentV1TypeEnum,
  type PimProductWithVariantsV1,
} from '@redteclab/api/clients/bully'
import { IconArrowRight, IconPdf } from '@redteclab/icons'

import { Button } from 'base-ui'

import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { productAccessorStockStatusIsAvailable } from '../../../model/productAccessor/productAccessorStockStatusIsAvailable'
import { useProductPageContext } from '../../productPageContext'

interface ProductDocumentLinks {
  hasDocumentLinks: boolean | undefined
  product: PimProductWithVariantsV1
  SERVICE_PDF_PATH: string
  shouldShowLinks: boolean | undefined
  shouldShowServiceLink: boolean | undefined
}

const getDocumentLabelWithType = (
  document: PimDocumentV1,
  productName: string,
): ReactNode => {
  const documentLabel = (
    <span className="break-words">{document.label ?? productName}</span>
  )

  switch (document.type) {
    case PimDocumentV1TypeEnum.ExternalBp:
    case PimDocumentV1TypeEnum.InternalBp: {
      return (
        <FormattedMessage
          id="product.link.label.packageInsert"
          values={{ productName: documentLabel }}
        />
      )
    }
    case PimDocumentV1TypeEnum.ExternalLv:
    case PimDocumentV1TypeEnum.InternalLv: {
      return (
        <FormattedMessage
          id="product.link.label.foodInformation"
          values={{ productName: documentLabel }}
        />
      )
    }
    case PimDocumentV1TypeEnum.InternalHw: {
      return (
        <FormattedMessage
          id="product.link.label.pharmaceuticalNotices"
          values={{ productName: documentLabel }}
        />
      )
    }
    default: {
      return documentLabel
    }
  }
}

const useProductDocumentLinks = (): ProductDocumentLinks => {
  const {
    product,
    productPageConfig: { servicePdfPath },
  } = useProductPageContext()
  const isAvailable = productAccessorStockStatusIsAvailable(product)
  const shouldShowPatientQuestionnaire =
    Boolean(product.flags?.medicament.active) && isAvailable
  const hasDocumentLinks = product.documents && product.documents.length > 0
  const shouldShowLinks = shouldShowPatientQuestionnaire || hasDocumentLinks

  const SERVICE_PDF_PATH =
    shouldShowLinks && servicePdfPath ? servicePdfPath : ''

  const hasServicePdfLink = SERVICE_PDF_PATH !== ''

  const shouldShowServiceLink =
    shouldShowPatientQuestionnaire && hasServicePdfLink

  return {
    hasDocumentLinks,
    product,
    SERVICE_PDF_PATH,
    shouldShowLinks,
    shouldShowServiceLink,
  }
}

const getDocumentLink = (
  href: string,
  content: string | ReactNode,
  isRx: boolean,
): ReactNode => {
  const endIconErx = isRx ? (
    <IconArrowRight className="fill-dark-brand-medium" />
  ) : undefined

  return (
    <Button<'a'>
      className={clsx(isRx && 'w-full bg-light-brand-medium p-3')}
      data-qa-id="product-details-file-for-download"
      endIcon={endIconErx}
      href={href}
      size="medium"
      slots={{ root: 'a' }}
      startIcon={<IconPdf />}
      target="_blank"
      variant="text"
    >
      <span className={clsx('whitespace-normal text-left', isRx && 'w-full')}>
        {content}
      </span>
    </Button>
  )
}

export const ProductDescriptionDocumentLinks: FC = () => {
  const {
    hasDocumentLinks,
    product,
    SERVICE_PDF_PATH,
    shouldShowLinks,
    shouldShowServiceLink,
  } = useProductDocumentLinks()

  const intl = useIntl()
  const hasIfapSections = product.ifap?.active ?? false
  const hasSplitAttributes: boolean = product.has_split_attributes ?? false

  const isPrescriptionProduct = productAccessorIsPrescriptionProduct(product)
  const showNewDocumentDesign: boolean =
    (hasIfapSections || hasSplitAttributes) && isPrescriptionProduct

  if (!shouldShowLinks) {
    return null
  }

  return (
    <ul
      className={clsx(
        'mb-4 flex flex-col gap-4',
        showNewDocumentDesign && 'mt-4',
      )}
    >
      {shouldShowServiceLink ? (
        <li>
          {getDocumentLink(
            SERVICE_PDF_PATH,
            intl.formatMessage({
              id: 'general.link.label.medicineService',
            }),
            showNewDocumentDesign,
          )}
        </li>
      ) : null}
      {hasDocumentLinks
        ? product.documents?.map((document, index) => {
            return (
              // eslint-disable-next-line react/no-array-index-key -- duplicate elements with no other way to differentiate between them
              <li key={`${document.url}-${index}`}>
                {getDocumentLink(
                  document.url,
                  getDocumentLabelWithType(document, product.name),
                  showNewDocumentDesign,
                )}
              </li>
            )
          })
        : null}
    </ul>
  )
}
